import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastProps {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
    onDismiss: (id: string) => void;
}

const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-400" />,
    error: <AlertCircle className="w-5 h-5 text-red-400" />,
    info: <Info className="w-5 h-5 text-blue-400" />,
};

const bgStyles = {
    success: 'bg-green-500/10 border-green-500/20',
    error: 'bg-red-500/10 border-red-500/20',
    info: 'bg-blue-500/10 border-blue-500/20',
};

const ToastItem: React.FC<ToastProps> = ({ id, type, message, duration = 3000, onDismiss }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onDismiss(id);
        }, duration);
        return () => clearTimeout(timer);
    }, [id, duration, onDismiss]);

    return (
        <div className={`flex items-center gap-3 p-4 rounded-lg border shadow-lg backdrop-blur-md transition-opacity duration-300 opacity-100 ${bgStyles[type]} w-full max-w-sm`}>
            {icons[type]}
            <p className="text-sm font-medium text-white flex-1">{message}</p>
            <button onClick={() => onDismiss(id)} className="text-slate-400 hover:text-white transition-colors">
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

export const ToastContainer: React.FC<{ toasts: ToastProps[]; removeToast: (id: string) => void }> = ({ toasts, removeToast }) => {
    return (
        <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
            <div className="pointer-events-auto flex flex-col gap-2">
                {toasts.map(toast => (
                    <ToastItem key={toast.id} {...toast} onDismiss={removeToast} />
                ))}
            </div>
        </div>
    );
};
